/**
 * SQL tab — the artifact's draft SQL body.
 *
 * Rendered through {@link CodeWithKnowledgeLinks} so concept-group ids in the
 * SQL stay clickable (open the concept-group details dialog). The body sits in
 * a shared {@link ArtifactTabCard} so the AV SQL surface matches the gen-3
 * cohort SQL tab.
 *
 * Copy-to-clipboard lives here (top-right of the SQL block, beside the code)
 * rather than on the drawer header — putting it on the header was ambiguous
 * (copy what? the description? the manifest?). Sitting it over the SQL block
 * makes the scope unambiguous: the SQL is what gets copied.
 */

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { ArtifactTabCard } from './ArtifactTabCard';
import type { EpiArtifact } from '../../types/epi-chat-types';
import { CodeWithKnowledgeLinks } from '../CodeWithKnowledgeLinks';

export const SqlTab = ({ artifact }: { artifact: EpiArtifact }) => {
  const { formatMessage } = useIntl();
  const [copied, setCopied] = useState(false);
  // No SQL body and nothing arriving → empty state. The "waiting for content…"
  // placeholder below is reserved for live streaming (status === 'streaming').
  if (!artifact.content && artifact.status !== 'streaming') {
    return (
      <ArtifactTabCard data-testid="artifact-sql-tab">
        <Box
          sx={{
            minHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 2,
          }}
        >
          <Typography variant="h5">{formatMessage({ id: 'epiChat.sqlTab.empty' })}</Typography>
          <Typography variant="body1" color="text.secondary">
            {formatMessage({ id: 'epiChat.sqlTab.emptySubtitle' })}
          </Typography>
        </Box>
      </ArtifactTabCard>
    );
  }
  const body = artifact.content || '-- (waiting for content…)';
  const canCopy = !!artifact.content;
  const handleCopy = () => {
    if (!canCopy) return;
    void navigator.clipboard?.writeText(artifact.content);
    setCopied(true);
    // Brief affordance — flip the icon to a check, then back. Two seconds
    // is the standard "you successfully copied" cue across the platform.
    window.setTimeout(() => setCopied(false), 2000);
  };
  return (
    <ArtifactTabCard data-testid="artifact-sql-tab">
      {/* Single row: the SQL starts at the top of the card with Copy SQL
          beside it, instead of a header row pushing the code down. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <CodeWithKnowledgeLinks
            language={artifact.artifactType === 'sql' ? 'sql' : 'text'}
            wrapLongLines
            code={body}
            customStyle={{
              margin: 0,
              padding: 0,
              fontSize: '1rem',
              lineHeight: 1.55,
              background: 'transparent',
            }}
          />
        </Box>
        <Button
          id="copy-sql"
          size="small"
          variant="text"
          startIcon={<ContentCopyIcon />}
          displayLabel={formatMessage({
            id: copied ? 'epiChat.sqlTab.copied' : 'epiChat.sqlTab.copySql',
          })}
          onClickHandler={handleCopy}
          disabled={!canCopy}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        />
      </Box>
    </ArtifactTabCard>
  );
};
